import { findIndex } from '@seiue/util'
import { useAtomValue } from 'jotai'
import { useContext, useEffect } from 'react'
import styled from 'styled-components'

import { RichTextRenderer } from 'packages/components/RichText'
import { BlockStateEnum } from 'packages/feature-utils/test-paper'
import { BlockTypeEnum, QuestionTypeEnum } from 'packages/sdks-next/growp'

import { getSubQuestionsByParentId } from '@/uikit/TestPaper/utils'

import { focusedBlockAtom } from '../../../BlockList/atoms'
import { HandoutBlockListContext } from '../../../BlockList/context'
import { BlockComponent } from '../../../BlockWrapper/types'
import { EditQuestion } from '../EditQuestion'

/**
 * 题目块内容
 *
 * @param props - Argument Object
 * @param props.state - 块状态
 * @param props.blockData - 块数据
 * @param props.onChangeState - 改变块状态
 * @param props.canEdit - 是否可编辑
 * @returns QuestionBlock
 */
export const QuestionBlock: BlockComponent = (props) => {
  const {
    state,
    blockData,
    onChangeState,
    canEdit,
    solutionExpanded,
    onSolutionClick,
  } = props
  const { blocks } = useContext(HandoutBlockListContext)
  const focusedBlock = useAtomValue(focusedBlockAtom)
  // 获取当前题目序号
  const getQuestionNum = () => {
    const questionBlocks = blocks?.filter(
      (b) => b.type === BlockTypeEnum.Question
    )
    const index = findIndex(questionBlocks, { id: blockData.id })
    return index + 1
  }
  const mainQuestion = blockData.questions?.find(
    (question) => question.parentId === 0
  )

  const answersFormat = (
    mainQuestionId: number,
    isProduct = false,
    isAnlyze = false
  ) => {
    const subQuestions = getSubQuestionsByParentId(
      blockData.questions || [],
      mainQuestionId
    )
    if (subQuestions.length <= 0) return ''
    const allAnswerList: string[] = []
    subQuestions.map((question, index) => {
      if (
        question.type === QuestionTypeEnum.FillInBlank &&
        isProduct &&
        !isAnlyze
      ) {
        const subQuestions = getSubQuestionsByParentId(
          blockData.questions || [],
          question.id
        )
        if (subQuestions.length > 0) {
          allAnswerList.push(`<div>(${index + 1})</div>`)
          subQuestions.map((q, i) => {
            allAnswerList.push(`
            <div style="margin-left: 15px">
              <span
                style="
                  display: inline-block;
                  width: 18px;
                  height: 18px;
                  border-radius: 50%;
                  border: 1px solid #718999;
                  border-color: #718999;
                  color: #718999;
                  font-size: 12px;
                  line-height: 18px;
                  text-align: center;
                  flex-shrink: 0;
                  transform: scale(0.8);
                  font-weight: 500;
                "
              >
              ${i + 1}
              </span>
              ${
                isAnlyze
                  ? question.analysis?.replace(/p>/g, 'span>') || '-'
                  : q.standardAnswers?.join('或').replace(/p>/g, 'span>') || '-'
              }
            </div>
          `)
          })
        }
        return
      }
      allAnswerList.push(`
        <div>
          ${
            isProduct
              ? `(${index + 1})`
              : `<span
                style="
                  display: inline-block;
                  width: 18px;
                  height: 18px;
                  border-radius: 50%;
                  border: 1px solid #718999;
                  border-color: #718999;
                  color: #718999;
                  font-size: 12px;
                  line-height: 18px;
                  text-align: center;
                  flex-shrink: 0;
                "
              >
                ${index + 1}
              </span>`
          }
          ${
            isAnlyze
              ? question.analysis?.replace(/p>/g, 'span>') || '-'
              : question.standardAnswers?.join('、').replace(/p>/g, 'span>') ||
                '-'
          }
        </div>
      `)
    })
    return allAnswerList.join('')
  }

  const getAnswers = (isAnlyze = false) => {
    if (mainQuestion?.type === QuestionTypeEnum.FillInBlank) {
      return isAnlyze
        ? mainQuestion.analysis
        : answersFormat(mainQuestion.id, false, isAnlyze)
    }
    if (mainQuestion?.type === QuestionTypeEnum.Product) {
      return answersFormat(mainQuestion.id, true, isAnlyze)
    }
    return isAnlyze
      ? mainQuestion?.analysis
      : mainQuestion?.standardAnswers?.join('、') || '-'
  }

  const subQuestions = getSubQuestionsByParentId(
    blockData.questions || [],
    mainQuestion?.id || 0
  )

  useEffect(() => {
    if (focusedBlock.id !== blockData.id) {
      if (state === BlockStateEnum.Editing) {
        props.onCancel()
      }
      onSolutionClick?.(false)
    }
  }, [focusedBlock.id])

  switch (state) {
    case BlockStateEnum.Display:
    case BlockStateEnum.Selected:
      return (
        <Content
          onClick={() => onChangeState(BlockStateEnum.Selected)}
          onDoubleClick={
            canEdit ? () => onChangeState(BlockStateEnum.Editing) : undefined
          }
        >
          <QuestionWrapper>
            <QuestionNum>{getQuestionNum()}.</QuestionNum>
            <StemWrapper>
              <RichTextRenderer value={mainQuestion?.stem || ''} />
              {mainQuestion?.type === QuestionTypeEnum.Product &&
              subQuestions.length > 0 ? (
                <>
                  {subQuestions.map((subQuestion, index) => {
                    if (!subQuestion?.stem) return null
                    return (
                      <SubStemWrapper key={subQuestion.id}>
                        <QuestionNum>({index + 1})</QuestionNum>
                        <RichTextRenderer value={subQuestion?.stem || ''} />
                      </SubStemWrapper>
                    )
                  })}
                </>
              ) : null}
            </StemWrapper>
          </QuestionWrapper>
          {solutionExpanded ? (
            <AnalysisWrapper>
              <AnalysisItem>
                <Tag>答案</Tag>
                <RichTextRenderer value={getAnswers() || '-'} />
              </AnalysisItem>
              <AnalysisItem>
                <Tag>解析</Tag>
                <RichTextRenderer value={getAnswers(true) || '-'} />
              </AnalysisItem>
            </AnalysisWrapper>
          ) : null}
        </Content>
      )
    case BlockStateEnum.Editing:
      return (
        <EditQuestion
          {...props}
          questions={blockData.questions}
        />
      )
    default:
      return null
  }
}

export const Content = styled.div`
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const QuestionWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
`

export const QuestionNum = styled.div`
  width: 24px;
  height: 22px;
  text-align: center;
  font-size: 14px;
  line-height: 28px;
  color: ${(props) => props.theme.text._1};
  flex-shrink: 0;
`

export const AnalysisWrapper = styled.div`
  width: 100%;
  padding: 12px 12px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.background._4};
  display: flex;
  flex-direction: column;
  gap: 9px;
  flex-shrink: 0;
`

const AnalysisItem = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 9px;
`

const Tag = styled.div`
  width: 40px;
  height: 22px;
  border-radius: 4px;
  padding: 1px 8px;
  font-size: 12px;
  line-height: 20px;
  color: ${(props) => props.theme.text._1};
  background-color: ${(props) => props.theme.background._2};
  flex-shrink: 0;
`

const StemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 8px;
`

const SubStemWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`
